<template>
<b-modal id="battle-modal" size="lg" :title="$t(battleModalTitle)">
  
    <div class="text-center">
        <div v-if="modalType == 'battleCreate'" class="alert alert-secondary" role="alert">         
             {{ 'BATTLE.CREATE_ALERT_TEXT' | translate }}
            <div class="sub-text">
                 {{ 'BATTLE.CREATE_ALERT_SUBTEXT' | translate }}            
            </div>
        </div>
        <div v-else class="alert alert-secondary" role="alert">
            {{ 'BATTLE.COMMENCE_ALERT_TEXT' | translate }}          
            <div class="sub-text">
                {{ 'BATTLE.COMMENCE_ALERT_SUBTEXT' | translate }}               
            </div>
        </div>
        <div class="row content-row">
            <div class="col-md-6">

                <CardTable 
                    :cards="cards" 
                    hasBetColumn="true"
                    :selectedBet="selectedBet" 
                    @update="updateSelectedBet" 
                    maxSelection="3" />

                <div class="input-container">
                    <div class="row no-gutter">
                        <div v-if="modalType == 'battleCreate'" class="col">
                            <header class="text-left">
                                {{ 'CREATED_ON' | translate }}
                            </header>
                            <input v-if="modalType == 'battleCreate'" type="text" readonly v-model="today"/>
                            <input v-else type="text" readonly v-model="battle.createdOn"/>
                        </div>
                        <div v-else class="col">
                            <header>
                                {{ 'BATTLE.OWNER_SCORE' | translate }}
                            </header>
                            <div>{{ battle.teamScore }}</div>
                        </div>
                        <div v-if="modalType != 'battleCreate'" class="versus-tag">
                            <span>
                                {{ 'VS' | translate }}
                            </span>
                        </div>
                        <div class="col">
                            <header>
                                {{ 'BATTLE.YOUR_SCORE' | translate }}
                            </header>
                            <div>{{ getTeamScore(selectedCards) }}</div>
                        </div>
                    </div>
                    <div v-if="modalType != 'battleCreate'" class="row no-gutter info-container">
                        <div class="col">                          
                            {{ 'BATTLE.CANNOT_EXCEED' | translate }}
                        </div>
                    </div>
                </div>                
            </div>
            <div class="col-md-5 offset-md-1">
                <div class="card-container">
                    <Card v-for="(card, index) in selectedCards" :key="index" :card="card" actionOn="false"/>
                    <Card v-if="selectedCards.length <= 0" type="new" card="{}" actionOn="false"/>
                </div>               
            </div>
        </div> 
    </div>
    <div slot="modal-footer" class="text-center">
        <button type="button" class="btn btn-secondary" @click="hideModal">
            {{ 'CLOSE' | translate }}
        </button>
        <button v-if="modalType == 'battleCreate'" type="button" class="btn btn-primary" @click="createBattle">
            {{ 'CREATE' | translate }}
        </button>
        <button v-else type="button" class="btn btn-primary" @click="commenceBattle">
            {{ 'BATTLE.COMMENCE' | translate }}
        </button>
    </div>

</b-modal>
</template>
 
<script>
import { mapGetters } from "vuex";
import { transformDna, getTeamScore } from '../util/transformDna';
import { CHARACTERS, ELEMENTS } from "../util/constants/cards";
import { battleLogic } from "../util/battleLogic";
import normalizeData from "../util/normalizeData";
import fees from "../util/constants/fees";
import CardTable from "./CardTable";
import Card from "./Card";

export default {
    name: "modal-battle",
    components: { Card, CardTable },
    data () {
        return {
            selectedBet: '',
            fees: fees,
            deadline: new Date()
        }
    },
    computed: {
        ...mapGetters({
            cards: 'getCards',  
            battle: 'getBattle',
            modalType: 'getModalType',
            selectedCards: 'getSelectedCards'
        }),
        battleModalTitle () {
            return this.modalType == 'battleCreate' ? 'BATTLE.CREATE' : 'BATTLE.COMMENCE_BTL'
        },
        today () {
            return this.$moment(new Date()).format('MM/DD/YYYY');
        }
    },
    methods: {
        updateSelectedBet (value){
            this.selectedBet = value;
        },
        hideModal () {
            this.$root.$emit('bv::hide::modal','battle-modal');
        },
        createBattle () {
            
            // Require 3 cards for battle
            if (this.selectedCards.length < 3) {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "You must select 3 cards!"
                });

                return;
            }

            // Require 1 card bet
            if (this.selectedBet == '') {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "You must select 1 bet!"
                });

                return;
            }

            const cardBet = this.selectedBet;
            const cardIds = [ 
                this.selectedCards[0].id, 
                this.selectedCards[1].id, 
                this.selectedCards[2].id
            ];

            // Close modal
            this.hideModal();

            // Update pending status
            this.$store.commit('updatePendingTransaction', { status: true });

            // Update lock status on bet card, before transaction completes
            this.$store.commit('updateCardLockStatus', { id: cardBet, isLocked: true, lockType: 'battle' });

            // Deselect radio
            this.selectedBet = '';

            // Initiate createBattle api call
            this.$store.dispatch('createBattle', { cardBet, cardIds }).then((response) => {

                // Update pending status
                this.$store.commit('updatePendingTransaction', { status: false });  

                if (response.message == 'error') {

                    this.$notify({
                        group: 'app',
                        title: 'Error!',
                        text: 'There was a problem with your transaction'
                    });

                    // Remove lock if error
                    this.$store.commit('updateCardLockStatus', { id: cardBet, isLocked: false, lockType: '' });

                } else {

                    this.$notify({
                        group: 'app',
                        title: 'Congrats!',
                        text: 'You have successfully created a new battle!'
                    });

                }
                
            });

        },
        commenceBattle () {
            const battle = this.battle;

            // Cannot fight yourself
            if (battle.owner == this.account) {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "You are the owner of this battle, and you cannot battle yourself!"
                });

                return;
            }

            // Require 3 cards for battle
            if (this.selectedCards.length < 3) {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "You must select 3 cards!"
                });

                return;
            }

            // Require 1 card bet
            if (this.selectedBet == '') {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "You must select 1 bet!"
                });

                return;
            }
            
            // Make sure opponent score isn't too high
            const teamScore = getTeamScore(this.selectedCards);    
            if (teamScore - battle.teamScore > 50) {
                this.$notify({
                    group: "app",
                    title: "Warning!",
                    text: "Your score cannot exceed the owner's score by more than 50 points!"
                });

                return;
            }

            const cardBet = this.selectedBet;
            const cardIds = [
                this.selectedCards[0].id,
                this.selectedCards[1].id,
                this.selectedCards[2].id
            ];
         
            // Close modal
            this.hideModal();

            // Update pending status
            this.$store.commit('updatePendingTransaction', { status: true });

            // Initiate createBattle api call
            this.$store.dispatch('commenceBattle', { battle, cardBet, cardIds }).then((response) => {

                // Update pending status
                this.$store.commit('updatePendingTransaction', { status: false });  
                this.selectedBet = '';

                if (response.message == 'error') {

                    this.$notify({
                        group: 'app',
                        title: 'Error!',
                        text: 'There was a problem with your transaction'
                    });

                    // Remove lock if error
                    this.$store.commit('updateCardLockStatus', { id: cardBet, isLocked: false, lockType: '' });

                } else {

                    const battleMessage = response.battleResult.winner == 'opponent' ? 'won' : 'lost';
                    this.$notify({
                        group: 'app',
                        title: 'Results!',
                        text: 'You have ' + battleMessage + ' the battle!',
                        duration: 10000
                    });

                }
                
            });

        },  
        getTeamScore (cards) {
            return cards.length <= 0 ? 0 : getTeamScore(cards);
        }
    }
};
</script>

<style scoped lang="less">
@import "../assets/less/app.less";
@import "../assets/less/modal.less";
</style>




// WEBPACK FOOTER //
// src/components/ModalBattle.vue